import React, { useState, useEffect } from "react";

import CloseSVG from "../../../../images/icons/svg/close.svg";
import paigeReviewService from "../../../../services/PaigeReview";
import FindingCard from "./FindingCard";

import styles from "./PaigeReview.module.scss";

const PaigeReview = (props) => {
  const { docId, closePanel, onFindingClick, onAnalysisUpdate } = props;

  // State
  const [analysis, setAnalysis] = useState(null);
  const [analysisType, setAnalysisType] = useState(null); // "paab" or "fda"
  const [isLoading, setIsLoading] = useState(true);

  // Load analysis on mount
  useEffect(
    function () {
      loadAnalysis();
    },
    [docId]
  );

  // Notify parent when analysis changes
  useEffect(
    function () {
      if (analysis && onAnalysisUpdate) {
        onAnalysisUpdate(analysis);
      }
    },
    [analysis]
  );

  // Poll for status when analysis is pending or processing
  useEffect(
    function () {
      if (!analysis || !analysisType) return;
      if (analysis.status !== "pending" && analysis.status !== "processing") {
        return;
      }

      var pollInterval = setInterval(function () {
        var getAnalysisFn =
          analysisType === "fda"
            ? paigeReviewService.getFdaAnalysis
            : paigeReviewService.getPaabAnalysis;

        getAnalysisFn(analysis.id)
          .then(function (response) {
            setAnalysis(response.data);
          })
          .catch(function (error) {
            console.error("Error polling analysis status:", error);
          });
      }, 3000);

      return function () {
        clearInterval(pollInterval);
      };
    },
    [analysis, analysisType]
  );

  var loadAnalysis = function () {
    setIsLoading(true);

    // Fetch both PAAB and FDA analyses in parallel
    Promise.all([
      paigeReviewService
        .getPaabAnalysesForDocument(docId)
        .catch(function (err) {
          console.log("PAAB analyses fetch failed:", err);
          return { data: [] };
        }),
      paigeReviewService.getFdaAnalysesForDocument(docId).catch(function (err) {
        console.log("FDA analyses fetch failed:", err);
        return { data: [] };
      }),
    ])
      .then(function (results) {
        var paabAnalyses = results[0].data || [];
        var fdaAnalyses = results[1].data || [];

        // Determine which analysis to use (most recent from either)
        var selectedAnalysis = null;
        var selectedType = null;

        if (paabAnalyses.length > 0 && fdaAnalyses.length > 0) {
          // Both exist - pick the most recent one
          var paabDate = new Date(paabAnalyses[0].created_at);
          var fdaDate = new Date(fdaAnalyses[0].created_at);
          if (fdaDate > paabDate) {
            selectedAnalysis = fdaAnalyses[0];
            selectedType = "fda";
          } else {
            selectedAnalysis = paabAnalyses[0];
            selectedType = "paab";
          }
        } else if (fdaAnalyses.length > 0) {
          selectedAnalysis = fdaAnalyses[0];
          selectedType = "fda";
        } else if (paabAnalyses.length > 0) {
          selectedAnalysis = paabAnalyses[0];
          selectedType = "paab";
        }

        if (!selectedAnalysis) {
          setIsLoading(false);
          return;
        }

        setAnalysisType(selectedType);

        // Fetch full analysis with findings
        var getAnalysisFn =
          selectedType === "fda"
            ? paigeReviewService.getFdaAnalysis
            : paigeReviewService.getPaabAnalysis;

        getAnalysisFn(selectedAnalysis.id)
          .then(function (response) {
            if (response && response.data) {
              setAnalysis(response.data);
            }
          })
          .catch(function (error) {
            console.error("Error fetching full analysis:", error);
          })
          .finally(function () {
            setIsLoading(false);
          });
      })
      .catch(function (error) {
        console.error("Error loading analyses list:", error);
        setIsLoading(false);
      });
  };

  var renderSummary = function () {
    if (!analysis) return null;

    return (
      <div className={styles.summary}>
        <div className={styles.summaryItem + " " + styles.critical}>
          <span className={styles.summaryCount}>{analysis.critical_count}</span>
          <span className={styles.summaryLabel}>Critical</span>
        </div>
        <div className={styles.summaryItem + " " + styles.warning}>
          <span className={styles.summaryCount}>{analysis.warning_count}</span>
          <span className={styles.summaryLabel}>Warnings</span>
        </div>
        <div className={styles.summaryItem + " " + styles.info}>
          <span className={styles.summaryCount}>{analysis.info_count}</span>
          <span className={styles.summaryLabel}>Info</span>
        </div>
      </div>
    );
  };

  var renderFindings = function () {
    if (!analysis || !analysis.findings) return null;

    var findings = analysis.findings;

    if (findings.length === 0) {
      return (
        <div className={styles.noFindings}>
          No issues found in this analysis.
        </div>
      );
    }

    // Sort by page number ascending
    var sortByPage = function (a, b) {
      var pageA = a.page_number != null ? a.page_number : Infinity;
      var pageB = b.page_number != null ? b.page_number : Infinity;
      return pageA - pageB;
    };

    // Group findings by severity and sort each group by page number
    var criticalFindings = findings
      .filter(function (f) {
        return f.severity === "critical";
      })
      .sort(sortByPage);
    var warningFindings = findings
      .filter(function (f) {
        return f.severity === "warning";
      })
      .sort(sortByPage);
    var infoFindings = findings
      .filter(function (f) {
        return f.severity === "info";
      })
      .sort(sortByPage);

    return (
      <div className={styles.findingsList}>
        {criticalFindings.length > 0 && (
          <div className={styles.findingsGroup}>
            <div className={styles.findingsGroupHeader}>Critical Issues</div>
            {criticalFindings.map(function (finding) {
              return (
                <FindingCard
                  key={finding.id}
                  finding={finding}
                  analysisType={analysisType}
                  onFindingClick={onFindingClick}
                />
              );
            })}
          </div>
        )}
        {warningFindings.length > 0 && (
          <div className={styles.findingsGroup}>
            <div className={styles.findingsGroupHeader}>Warnings</div>
            {warningFindings.map(function (finding) {
              return (
                <FindingCard
                  key={finding.id}
                  finding={finding}
                  analysisType={analysisType}
                  onFindingClick={onFindingClick}
                />
              );
            })}
          </div>
        )}
        {infoFindings.length > 0 && (
          <div className={styles.findingsGroup}>
            <div className={styles.findingsGroupHeader}>Info</div>
            {infoFindings.map(function (finding) {
              return (
                <FindingCard
                  key={finding.id}
                  finding={finding}
                  analysisType={analysisType}
                  onFindingClick={onFindingClick}
                />
              );
            })}
          </div>
        )}
      </div>
    );
  };

  var renderContent = function () {
    if (isLoading) {
      return (
        <div className={styles.loadingState}>
          <div className={styles.spinner}></div>
          <div>Loading analysis...</div>
        </div>
      );
    }

    if (!analysis) {
      return (
        <div className={styles.emptyState}>
          <div className={styles.emptyStateTitle}>No analysis available</div>
          <div className={styles.emptyStateText}>
            This document has not been analyzed yet.
          </div>
        </div>
      );
    }

    if (analysis.status === "pending" || analysis.status === "processing") {
      return (
        <div className={styles.processingState}>
          <div className={styles.spinner}></div>
          <div className={styles.processingTitle}>Analysis in progress</div>
          <div className={styles.processingText}>
            This may take a few minutes. The results will appear automatically
            when complete.
          </div>
        </div>
      );
    }

    if (analysis.status === "failed") {
      return (
        <div className={styles.errorState}>
          <div className={styles.errorTitle}>Analysis failed</div>
          <div className={styles.errorText}>
            {analysis.error_message || "An error occurred during analysis."}
          </div>
        </div>
      );
    }

    return (
      <>
        {renderSummary()}
        {renderFindings()}
      </>
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.title}>Paige Review</div>
        </div>
        <div className={styles.headerRight}>
          <div className={styles.closeButton} onClick={closePanel}>
            <img src={CloseSVG} alt="Close" />
          </div>
        </div>
      </div>

      <div className={styles.body}>{renderContent()}</div>
    </div>
  );
};

export default PaigeReview;
